import { useCallback } from 'react';
import { useTranslationContext, fallbackLocalizedText } from './TranslationProvider.js';
import insertPlaceholders from './insertPlaceholders.js';
import containsComponentOrElement from './containsComponentOrElement.js';

/* eslint-disable babel/no-unused-expressions */
const NO_LOCALIZED_TEXT_FOR_KEY = key => "There is no localized text for KEY: ".concat(key, ". Defaulting to fallback text");
const NO_LOCALIZED_OR_FALLBACK_TEXT_FOR_KEY = key => "There is no localized or fallback text for KEY: ".concat(key, ". \n    Please refer to https://designsystem.qualtrics.com/localization for more information about localizing Qualtrics ui-react components");
const USETRANSLATION_PLACEHOLDER_CONTAINS_ELEMENT = 'The useTranslation function cannot include React elements as placeholders.';
const HAS_ERROR_BEEN_LOGGED_FOR_KEY = new Set();

/**
 * Returns a translate function.
 * @returns A translate function.
 */
const useTranslation = () => {
  const {
    localizedText
  } = useTranslationContext();

  /**
   * Returns a translated string given a key and placeholders.
   * @param key - The key of the translated string.
   * @param placeholders - A map of parameters to replace in the translated string. The string is searched for each key in the map wrapped by {{}} and then replaced with the corresponding value. *Placeholders can't be components. To use a component placeholder, use the `Translate` component.*
   * @returns The translated string if found; otherwise `##${translation-key}##`.
   */
  const translate = useCallback((key, placeholders) => {
    const translatedText = localizedText === null || localizedText === void 0 ? void 0 : localizedText[key];
    const fallbackText = fallbackLocalizedText[key];
    if (!translatedText && !HAS_ERROR_BEEN_LOGGED_FOR_KEY.has(key)) {
      HAS_ERROR_BEEN_LOGGED_FOR_KEY.add(key);
      if (fallbackText) {
        console.warn(NO_LOCALIZED_TEXT_FOR_KEY(key));
      } else {
        console.warn(NO_LOCALIZED_OR_FALLBACK_TEXT_FOR_KEY(key));
      }
    }
    const text = translatedText || fallbackText;
    if (text && placeholders) {
      if (containsComponentOrElement(placeholders)) {
        console.warn(USETRANSLATION_PLACEHOLDER_CONTAINS_ELEMENT);
        return '';
      }
      const compiledMessage = insertPlaceholders(text, placeholders);
      return compiledMessage.join('');
    }
    return text || "##".concat(key, "##");
  }, [localizedText]);
  return {
    translate
  };
};

export { useTranslation as default };
